import { useState, useEffect, useCallback } from 'react';
import { RBAC_ENDPOINTS } from '../config/api';
import { rbacFetch } from '../utils/apiClient';

/**
 * usePatientMilestones
 * Fetches patient milestone data from the RBAC backend.
 * Supports filtering, pagination, and sorting.
 */
export function usePatientMilestones(initialParams = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [params, setParams] = useState({
    milestone_type: 'all',
    milestone_status: 'all',
    cr_status: 'all',
    hospital: '',
    physician: '',
    indication: '',
    rr_category: '',
    zone: '',
    search: '',
    page: 1,
    page_size: 10,
    sort_by: 'patient_id',
    sort_order: 'asc',
    ...initialParams,
  });

  const fetchMilestones = useCallback(async (queryParams) => {
    setLoading(true);
    setError(null);
    try {
      const searchParams = new URLSearchParams();
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          searchParams.set(key, value);
        }
      });

      const res = await rbacFetch(`${RBAC_ENDPOINTS.milestones}?${searchParams}`);
      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.message || 'Failed to load milestones');
      }

      setData(json.data);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on params change
  useEffect(() => {
    fetchMilestones(params);
  }, [params, fetchMilestones]);

  // Update params (merges with existing)
  const updateParams = useCallback((newParams) => {
    setParams(prev => {
      const updated = { ...prev, ...newParams };
      // Reset to page 1 if any filter changes (except page itself)
      if (!('page' in newParams)) {
        updated.page = 1;
      }
      return updated;
    });
  }, []);

  // Set page directly
  const setPage = useCallback((page) => {
    setParams(prev => ({ ...prev, page }));
  }, []);

  // Reset all filters
  const resetFilters = useCallback(() => {
    setParams({
      milestone_type: 'all',
      milestone_status: 'all',
      cr_status: 'all',
      hospital: '',
      physician: '',
      indication: '',
      rr_category: '',
      zone: '',
      search: '',
      page: 1,
      page_size: 10,
      sort_by: 'patient_id',
      sort_order: 'asc',
    });
  }, []);

  // Refresh data
  const refresh = useCallback(() => {
    fetchMilestones(params);
  }, [params, fetchMilestones]);

  return {
    data,
    loading,
    error,
    params,
    updateParams,
    setPage,
    resetFilters,
    refresh,
  };
}

/**
 * Fetch filter options (hospitals, physicians, indications, rr_categories)
 */
export async function fetchMilestoneFilterOptions() {
  const res = await rbacFetch(RBAC_ENDPOINTS.milestonesFilters);
  const json = await res.json();
  if (!res.ok) throw new Error(json.message || 'Failed to load filter options');
  return json.data;
}

/**
 * Fetch all milestone data for export (no pagination)
 */
export async function fetchMilestonesExport(params = {}) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== '' && value !== null && value !== undefined) {
      searchParams.set(key, value);
    }
  });

  const res = await rbacFetch(`${RBAC_ENDPOINTS.milestonesExport}?${searchParams}`);
  const json = await res.json();
  if (!res.ok) throw new Error(json.message || 'Failed to export milestones');
  return json.data;
}
